import { useTranslation } from 'react-i18next';
import { Box, ButtonBase, useTheme } from '@mui/material';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconClock,
  IconX,
} from '@tabler/icons-react';

import { Typography } from '@zendesk/forethought-elements';
import { TrialBanner } from './resolveTrialBanner';
import { TRIAL_BANNER_HEIGHT_PX } from './useTrialBanner';
import {
  insightGradients,
  TRIAL_ENDED_GRADIENT,
  TRIAL_EXPIRING_GRADIENT,
} from 'src/constants/colors';

const TONE_ICON = {
  active: IconClock,
  ended: IconAlertCircle,
  expiring: IconAlertTriangle,
} as const;

const TONE_GRADIENT = {
  active: insightGradients[15],
  ended: TRIAL_ENDED_GRADIENT,
  expiring: TRIAL_EXPIRING_GRADIENT,
} as const;

interface TrialTopBannerProps {
  banner: TrialBanner;
  onDismiss: () => void;
}

export const TrialTopBanner = ({ banner, onDismiss }: TrialTopBannerProps) => {
  const { t } = useTranslation('trial-banner');
  const { palette } = useTheme();

  const Icon = TONE_ICON[banner.tone];
  const backgroundImage = TONE_GRADIENT[banner.tone];
  // 14px bold is not WCAG "large text", so these need 4.5:1 against the
  // *darkest* stop of their gradient. yellow[800] only reaches 3.73:1 on the
  // expiring tint; yellow[900] clears it at 5.80:1.
  const color = {
    active: palette.colors.black,
    ended: palette.colors.red[700],
    expiring: palette.colors.yellow[900],
  }[banner.tone];

  return (
    <Box
      role='alert'
      sx={{
        alignItems: 'center',
        // The tints are translucent and assume white beneath them, so the
        // banner paints its own backing in both modes rather than branching on
        // the dark-mode flag.
        backgroundColor: palette.colors.white,
        backgroundImage,
        display: 'flex',
        flexShrink: 0,
        gap: 1,
        height: `${TRIAL_BANNER_HEIGHT_PX}px`,
        justifyContent: 'center',
        position: 'relative',
        width: '100%',
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          bgcolor: palette.colors.white,
          borderRadius: '12px',
          display: 'flex',
          height: '24px',
          justifyContent: 'center',
          width: '24px',
        }}
      >
        <Icon color={color} size={15} />
      </Box>
      <Typography color={color} variant='font14Bold'>
        {t(banner.copyKey, {
          count: banner.daysLeft,
          date: banner.endDate ?? '',
        })}
      </Typography>
      {banner.isDismissable && (
        <ButtonBase
          aria-label={t('dismiss')}
          onClick={onDismiss}
          sx={{
            borderRadius: '6px',
            height: '22px',
            position: 'absolute',
            right: '12px',
            width: '22px',
          }}
        >
          <IconX color={color} size={12} />
        </ButtonBase>
      )}
    </Box>
  );
};
